import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
//import SkateboardingIcon from '@mui/icons-material/Skateboarding';
import { LaserIcon, MagnifierIcon, TeleportIcon, MapIcon } from '@icons';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useToolsState } from '../hooks';
import { useMytaverse } from '@providers/MytaverseProvider';

import { ITool } from './interfaces';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { FeedbackIcon } from '@icons/FeedbackIcon';
import { shouldShowFeedbackForEvent } from '@core/dashboard/utils/feedbackFormUtils';
import { useMapModal } from '@core/dashboard/components/MapModal/context/context';
import {
  useClientSpecificTeleportVisibility,
  useClientSpecificLaserPointerVisibility,
  useClientSpecificMagnifierVisibility,
  useClientSpecificMapVisibility,
} from '@hooks/clientSpecificUtils';

export const useHandleToolClick = (onClose: () => void) => {
  const {
    isHoverboardApplied,
    setHoverboard,
    isLaserPointerApplied,
    setLaserPointer,
    isMagnifierApplied,
    setMagnifier,
  } = useToolsState();
  const { sendMessageToUnreal, setShowFeedbackForm } = useMytaverse();
  const { isTeleportingToRoomByUnreal } = useMytaverseEvent();

  const { toggleOpen } = useMapModal();

  const handleClick = (action: WebsocketAction) => {
    if (action === WebsocketAction.Hoverboard) {
      sendMessageToUnreal({
        action,
        state: isHoverboardApplied,
      });
      setHoverboard(!isHoverboardApplied);
      onClose();
      return;
    }

    if (action === WebsocketAction.LaserPointer) {
      sendMessageToUnreal({
        action,
        state: isLaserPointerApplied,
      });
      setLaserPointer(!isLaserPointerApplied);
      onClose();
      return;
    }

    if (action === WebsocketAction.Magnifier) {
      sendMessageToUnreal({
        action,
        state: isMagnifierApplied,
      });
      setMagnifier(!isMagnifierApplied);
      onClose();
      return;
    }

    if (action === WebsocketAction.BeamMeUp) {
      sendMessageToUnreal({
        action,
        state: isTeleportingToRoomByUnreal,
      });
      onClose();
    }

    if (action === WebsocketAction.ShowSurvey) {
      setShowFeedbackForm(true);
    }

    if (action === WebsocketAction.MButtonClick) {
      toggleOpen();
    }
  };

  return handleClick;
};

export const useTools = () => {
  const { t: translate } = useTranslation();
  const { teleportEnabled, currentEvent } = useMytaverseEvent();

  const { active: isHubMapActive } = useMapModal();
  const { isTeleportFeatureEnabled } = useClientSpecificTeleportVisibility();
  const { isLaserPointerEnabled } = useClientSpecificLaserPointerVisibility();
  const { isMagnifierEnabled } = useClientSpecificMagnifierVisibility();
  const { isMapEnabled } = useClientSpecificMapVisibility();

  const tools: ITool[] = [
    ...(teleportEnabled && isTeleportFeatureEnabled
      ? [
          {
            icon: TeleportIcon,
            name: translate(
              isMobileOnly ? 'actions.teleport' : 'actions.teleportT',
            ),
            description: translate('actions.teleportDescription'),
            action: WebsocketAction.BeamMeUp,
          },
        ]
      : []),
    // {
    //   icon: SkateboardingIcon,
    //   name: translate('actions.hoverboard'),
    //   description: translate('actions.hoverboardDescription'),
    //   action: WebsocketAction.Hoverboard,
    // },
    ...(isLaserPointerEnabled
      ? [
          {
            icon: LaserIcon,
            name: translate('actions.laserPointer'),
            description: translate('actions.laserPointerDescription'),
            action: WebsocketAction.LaserPointer,
          },
        ]
      : []),
    ...(isMagnifierEnabled
      ? [
          {
            icon: MagnifierIcon,
            name: translate('actions.magnifier'),
            description: translate('actions.magnifierDescription'),
            action: WebsocketAction.Magnifier,
          },
        ]
      : []),
    ...(shouldShowFeedbackForEvent(currentEvent?.eventTags)
      ? [
          {
            icon: FeedbackIcon,
            name: translate('actions.survey'),
            description: translate('actions.surveyDescription'),
            action: WebsocketAction.ShowSurvey,
          },
        ]
      : []),
    ...(isHubMapActive && isMapEnabled
      ? [
          {
            icon: MapIcon,
            name: translate(isMobileOnly ? 'actions.map' : 'actions.mapM'),
            description: translate('actions.mapDescription'),
            action: WebsocketAction.MButtonClick,
          },
        ]
      : []),
  ];

  return tools;
};
